import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { Stepper } from "@/components/ui/stepper";
import { BasicInfoStep } from "./wizard/BasicInfoStep";
import { LegalFiscalStep } from "./wizard/LegalFiscalStep";
import { B2BRequirementsStep } from "./wizard/B2BRequirementsStep";
import { useCreateStoreWizard } from "./wizard/useCreateStoreWizard";
import { Info } from "lucide-react";
import { useTranslation } from "react-i18next";
import { CreateStoreWizardFormValues } from "./wizard/types";
import { useAuthStore } from "@/store/authStore";

interface CreateStoreWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete?: (formValues: CreateStoreWizardFormValues) => void;
}

export function CreateStoreWizard({
  open,
  onOpenChange,
  onComplete,
}: CreateStoreWizardProps) {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const { form, step, steps, nextStep, prevStep } = useCreateStoreWizard(() => {
    onOpenChange(false);
    if (onComplete) {
      onComplete(form.getValues());
    }
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("createStoreWizard.createNewStore")}</DialogTitle>
        </DialogHeader>

        <Stepper steps={steps} currentStep={step} className="my-6" />

        {/* Information Block - Common to all steps */}
        <div className="bg-blue-50 p-4 rounded-lg mb-6 flex">
          <Info className="h-5 w-5 text-blue-500 mt-1 mr-2 shrink-0" />
          <div>
            <h3 className="font-medium text-lg">
              {t("createStoreWizard.whyWeNeedThis")}
            </h3>
            <p className="text-muted-foreground">
              {t("createStoreWizard.whyWeNeedThisDescription")}
            </p>
          </div>
        </div>

        <Form {...form}>
          <form className="space-y-6">
            {/* Step 1: Basic Information */}
            {step === 0 && <BasicInfoStep form={form} />}

            {/* Step 2: Legal & Fiscal Information */}
            {step === 1 && <LegalFiscalStep form={form} />}

            {/* Step 3: B2B Requirements */}
            {step === 2 && <B2BRequirementsStep form={form} />}
          </form>
        </Form>

        <DialogFooter className="pt-4">
          {step > 0 && (
            <Button variant="outline" onClick={prevStep}>
              {t("createStoreWizard.back")}
            </Button>
          )}
          <Button onClick={nextStep}>
            {step < steps.length - 1
              ? t("createStoreWizard.next")
              : t("createStoreWizard.createStore")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
